import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import Index from "./pages/Index";
import Login from "./pages/Login";
import PushNotificationBanner from "./components/PushNotificationBanner";
import WhatsAppWidget from "./components/WhatsAppWidget";
import CookieConsent from "./components/CookieConsent";
import GoogleAnalytics from "./components/GoogleAnalytics";
import PublicLayout from "./components/PublicLayout";

// Retry wrapper for lazy imports (handles transient 503 errors)
function lazyRetry(fn: () => Promise<any>, retries = 3): ReturnType<typeof lazy> {
  return lazy(() => {
    const attempt = (remaining: number): Promise<any> =>
      fn().catch((err: any) => {
        if (remaining > 0) {
          return new Promise<void>((r) => setTimeout(r, 1000)).then(() => attempt(remaining - 1));
        }
        window.location.reload();
        throw err;
      });
    return attempt(retries);
  });
}

const ForgotPassword = lazyRetry(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyRetry(() => import("./pages/ResetPassword"));
const RegistroCliente = lazyRetry(() => import("./pages/RegistroCliente"));
const Dashboard = lazyRetry(() => import("./pages/Dashboard"));
const Orcamentos = lazyRetry(() => import("./pages/Orcamentos"));
const Leads = lazyRetry(() => import("./pages/Leads"));
const Internacoes = lazyRetry(() => import("./pages/Internacoes"));
const Cupons = lazyRetry(() => import("./pages/Cupons"));
const MapaFamilia = lazyRetry(() => import("./pages/MapaFamilia"));
const Lembretes = lazyRetry(() => import("./pages/Lembretes"));
const Perfil = lazyRetry(() => import("./pages/Perfil"));
const Usuarios = lazyRetry(() => import("./pages/Usuarios"));
const DashboardLayout = lazyRetry(() => import("./components/DashboardLayout"));
const NotFound = lazyRetry(() => import("./pages/NotFound"));
const ClinicaDetalhe = lazyRetry(() => import("./pages/ClinicaDetalhe"));
const Clinicas = lazyRetry(() => import("./pages/Clinicas"));
const PainelClinica = lazyRetry(() => import("./pages/PainelClinica"));
const MapaAtendentes = lazyRetry(() => import("./pages/MapaAtendentes"));
const PesquisaRapida = lazyRetry(() => import("./pages/PesquisaRapida"));
const Ambulancias = lazyRetry(() => import("./pages/Ambulancias"));
const Fichas = lazyRetry(() => import("./pages/Fichas"));
const Visitas = lazyRetry(() => import("./pages/Visitas"));
const Documentos = lazyRetry(() => import("./pages/Documentos"));
const FichaPublica = lazyRetry(() => import("./pages/FichaPublica"));
const Sugestoes = lazyRetry(() => import("./pages/Sugestoes"));
const Notificacoes = lazyRetry(() => import("./pages/Notificacoes"));
const SeoConfig = lazyRetry(() => import("./pages/SeoConfig"));
const AnunciosHelper = lazyRetry(() => import("./pages/AnunciosHelper"));
const ClinicasEstado = lazyRetry(() => import("./pages/ClinicasEstado"));
const ClinicasCidade = lazyRetry(() => import("./pages/ClinicasCidade"));
const TermosDeUso = lazyRetry(() => import("./pages/TermosDeUso"));
const PoliticaPrivacidade = lazyRetry(() => import("./pages/PoliticaPrivacidade"));
const BlogPost = lazyRetry(() => import("./pages/BlogPost"));
const ContatoDpo = lazyRetry(() => import("./pages/ContatoDpo"));
const Sair = lazyRetry(() => import("./pages/Sair"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,   // 5 min — data is fresh
      gcTime: 10 * 60 * 1000,     // 10 min — keep in memory
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center text-muted-foreground">Carregando...</div>
);

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isReady } = useAuth();
  const location = useLocation();
  if (!isReady) return <div className="flex min-h-screen items-center justify-center text-muted-foreground">Carregando...</div>;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  return <>{children}</>;
};

const PublicOnlyRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isReady } = useAuth();
  const location = useLocation();
  if (!isReady) return <div className="flex min-h-screen items-center justify-center text-muted-foreground">Carregando...</div>;
  const from = (location.state as any)?.from || "/dashboard";
  if (user) return <Navigate to={from} replace />;
  return <>{children}</>;
};

const App = () => (
  <ThemeProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <WhatsAppWidget />
          <CookieConsent />
          <GoogleAnalytics />
          <AuthProvider>
            <PushNotificationBanner scope="global" />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public pages with shared footer */}
                {/* Auth pages without footer */}
                <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
                <Route path="/registro" element={<PublicOnlyRoute><RegistroCliente /></PublicOnlyRoute>} />
                <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPassword /></PublicOnlyRoute>} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/sair" element={<Sair />} />

                {/* Public pages with shared footer */}
                <Route element={<PublicLayout />}>
                  <Route path="/" element={<Index />} />
                  <Route path="/clinica/:id" element={<ClinicaDetalhe />} />
                  <Route path="/clinica/:estado/:cidade/:slug" element={<ClinicaDetalhe />} />
                  <Route path="/clinicas/:estado_slug" element={<ClinicasEstado />} />
                  <Route path="/clinicas/:estado_slug/:cidade_slug" element={<ClinicasCidade />} />
                  <Route path="/ficha/:token" element={<FichaPublica />} />
                  <Route path="/mapa" element={<MapaAtendentes />} />
                  <Route path="/termos-de-uso" element={<TermosDeUso />} />
                  <Route path="/politica-de-privacidade" element={<PoliticaPrivacidade />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/contato-dpo" element={<ContatoDpo />} />
                </Route>
                <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/clinicas" element={<Clinicas />} />
                  <Route path="/painel-clinica" element={<PainelClinica />} />
                  <Route path="/painel-clinica/leads" element={<PainelClinica />} />
                  <Route path="/orcamentos" element={<Orcamentos />} />
                  <Route path="/leads" element={<Leads />} />
                  <Route path="/internacoes" element={<Internacoes />} />
                  <Route path="/cupons" element={<Cupons />} />
                  <Route path="/mapa-familia" element={<MapaFamilia />} />
                  <Route path="/mapa-atendentes" element={<MapaAtendentes />} />
                  <Route path="/pesquisa" element={<PesquisaRapida />} />
                  <Route path="/ambulancias" element={<Ambulancias />} />
                  <Route path="/fichas" element={<Fichas />} />
                  <Route path="/visitas" element={<Visitas />} />
                  <Route path="/lembretes" element={<Lembretes />} />
                  <Route path="/documentos" element={<Documentos />} />
                  <Route path="/sugestoes" element={<Sugestoes />} />
                  <Route path="/notificacoes" element={<Notificacoes />} />
                  <Route path="/perfil" element={<Perfil />} />
                  <Route path="/usuarios" element={<Usuarios />} />
                  <Route path="/seo" element={<SeoConfig />} />
                  <Route path="/anuncios" element={<AnunciosHelper />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
